/* ==========================================================================
   HUSSIENI HOME — MOTION
   One IntersectionObserver for the whole document. No animation library:
   a fade-and-rise is two compositable properties, and pulling in a 100kB
   dependency for that would cost more than the effect is worth.

   Everything here is a no-op when the visitor prefers reduced motion, and
   a no-op when IntersectionObserver is unavailable — in both cases content
   is revealed immediately rather than left invisible.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Selectors that should reveal on scroll. Kept here rather than sprinkled
     through Liquid so the motion system stays in one place. */
  var TARGETS = [
    '.shopify-section:not(.shopify-section-group-footer-group) > .section',
    '.section__header',
    '.hh-reveal',
    '.hh-reveal--stagger',
    '.hh-reveal-media'
  ].join(',');

  function revealAll() {
    document.querySelectorAll('.hh-reveal, .hh-reveal--stagger, .hh-reveal-media')
      .forEach(function (el) { el.classList.add('is-visible'); });
  }

  function init() {
    // Honour the OS setting, and bail out safely on older browsers.
    if (REDUCED.matches || !('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        // Reveal is one-way: stop watching so we do no work on further scroll.
        observer.unobserve(entry.target);
      });
    }, {
      // Trigger slightly before the element reaches the viewport edge so the
      // movement has finished by the time it is properly in view.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.05
    });

    document.querySelectorAll(TARGETS).forEach(function (el) {
      // Anything already on screen at load must not fade in — that reads as
      // a slow page, not a considered one.
      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.9) {
        el.classList.add('is-visible');
        return;
      }
      if (!el.classList.contains('hh-reveal-media')) el.classList.add('hh-reveal');
      observer.observe(el);
    });

    // Index staggered children so each resolves a beat after the last.
    document.querySelectorAll('.hh-reveal--stagger').forEach(function (group) {
      Array.prototype.forEach.call(group.children, function (child, i) {
        // Cap the delay so a 40-product grid does not take four seconds.
        child.style.setProperty('--hh-i', Math.min(i, 8));
      });
    });
  }

  /* ---- Header: transparent over the hero, solid once scrolled ---------- */
  function initHeader() {
    var root = document.body;
    var ticking = false;

    function update() {
      root.classList.toggle('is-at-top', window.scrollY < 24);
      root.classList.toggle('hh-scrolled', window.scrollY >= 24);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      // Coalesce scroll events into one write per frame.
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });

    update();
  }

  function boot() { init(); initHeader(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // If the visitor changes the OS motion setting mid-session, respect it.
  if (REDUCED.addEventListener) {
    REDUCED.addEventListener('change', function (e) { if (e.matches) revealAll(); });
  }

  // Shopify's theme editor re-renders sections without a page load; new
  // markup would otherwise stay invisible.
  document.addEventListener('shopify:section:load', boot);
})();
